import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The reads for the Identity MCP screen (task 7.3; requirement 13.3, 13.6; design D1/D5).
 *
 * Two operator-API operations back the screen, both role `viewer`, taken straight from the
 * generated `@oathera/contract/api` and never redefined here (no stub data; requirement 13.6):
 *
 * - `GET /api/v1/identity-mcp/summary` → {@link IdentityMcpSummary}: operations by tool and outcome
 *   (zero-filled over the catalog `identity_mcp_tool` × `result` members), the p50/p95/p99 handler
 *   latency percentiles, Protected_Core state per instance, and the opt-in coverage counts
 *   (registered / reporting / dark). A `reason` carried by a `success` outcome is ignored server-side
 *   (FR-2.5), so the summary's operations are already clean. This is the PRIMARY snapshot the whole
 *   screen's top-level state rests on.
 * - `GET /api/v1/identity-mcp/denials?step=…` → {@link IdentityMcpDenialPage}: one page of denial
 *   rows, each a `reason` with a `total` and a per-step time `series`, zero-filled over every catalog
 *   `identity_mcp_reason` member so "nothing matched" reads differently from "no data" (FR-4.3). The
 *   denials are a SECONDARY region that shows its own loading/empty state inside the ready screen so
 *   a slow or empty denials read never blanks the page.
 *
 * Both windows are resolved from the shared time-range exactly as the Decisions/Identity screens do,
 * so pausing freezes every query: in live mode "now" is bucketed to a 10s grid to keep the query key
 * stable between ticks; when frozen (paused or custom) `resolveWindow` returns the exact frozen
 * window and the key stops advancing (design 12.2, FR-13 AC2). Fetches go through `lib/api.ts`, so a
 * cold-core 503 surfaces as a `waking` {@link ApiError} and the shell shows "Waking the control
 * plane"; queries are retried per `query-client.ts`.
 */

/** The contract `IdentityMcpSummary` response body. */
export type IdentityMcpSummary = components["schemas"]["IdentityMcpSummary"];
/** The contract `IdentityMcpDenialPage` response body. */
export type IdentityMcpDenialPage = components["schemas"]["IdentityMcpDenialPage"];

/** The resolved time window a query ran for, so the screen can label the snapshot honestly. */
export interface IdentityMcpWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseIdentityMcpSummaryResult {
  readonly query: UseQueryResult<IdentityMcpSummary, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: IdentityMcpWindow;
}

export interface UseIdentityMcpDenialsResult {
  readonly query: UseQueryResult<IdentityMcpDenialPage, ApiError>;
  readonly window: IdentityMcpWindow;
}

/** Resolve the current window from the time-range (live buckets to 10s; frozen is exact). */
function useResolvedWindow(): IdentityMcpWindow {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  return { fromMs: resolved.fromMs, toMs: resolved.toMs };
}

/**
 * Fetch the Identity MCP summary for the current window. The query key carries the window so a
 * frozen window never refetches and a live window advances on the 10s grid; `staleTime`/
 * `refetchInterval` match the other screens (live tolerates a little staleness, frozen never goes
 * stale).
 */
export function useIdentityMcpSummary(): UseIdentityMcpSummaryResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<IdentityMcpSummary, ApiError>({
    queryKey: ["identity-mcp-summary", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<IdentityMcpSummary>("/identity-mcp/summary", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch the denials-by-reason page for the current window. The over-time `series` on each row is
 * bucketed by the operation's `step` — resolved from the window span so the stacked chart has a
 * sensible number of buckets regardless of the preset — and `step` is part of the query key so it
 * stays stable. The window freezes with the summary on pause.
 */
export function useIdentityMcpDenials(): UseIdentityMcpDenialsResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();
  const stepS = stepForWindow(window);

  const query = useQuery<IdentityMcpDenialPage, ApiError>({
    queryKey: ["identity-mcp-denials", window.fromMs, window.toMs, stepS],
    queryFn: ({ signal }) =>
      apiFetch<IdentityMcpDenialPage>("/identity-mcp/denials", {
        query: { from: window.fromMs, to: window.toMs, step: stepS },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * A sensible `step` (bucket size, seconds) for the denial series given the window span, aiming for
 * roughly 90 buckets so the stacked chart is dense but not noisy. Clamped to the contract-friendly
 * 10s..1h range. Kept here so it is part of the query key and therefore stable per window.
 */
function stepForWindow(window: IdentityMcpWindow): number {
  const spanMs = Math.max(0, window.toMs - window.fromMs);
  const targetBuckets = 90;
  const rawStepS = spanMs / targetBuckets / 1000;
  const choices = [10, 30, 60, 300, 600, 1800, 3600];
  for (const choice of choices) {
    if (rawStepS <= choice) return choice;
  }
  return choices[choices.length - 1] as number;
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
